import React, { useCallback, useEffect, useRef, useState } from "react";
import "../DrugAssignment.css";
import { Link, useNavigate } from "react-router-dom";
import { Divider, Grid2 } from "@mui/material";
import SearchedComponent from "../../../components/SearchedComponent";
import Loader from "../../../components/Loader";
import { InfoOutlined } from "@mui/icons-material";
import {
  fetchAllIndividualDrugService,
  fetchOnLoadDataService,
  saveIndividualDrugDataService,
} from "../../../services/drugAssignmentService";
import {
  isEmptyObject,
  notifyError,
  notifySuccess,
} from "../../../utilities/helper";
import IndividualConfirmPage from "./IndividualConfirmPage";
import UncontrolledSearchSelect from "../../../components/UncontrolledSearchSelect";
import GridWithRef from "../../../components/GridWithRef";

const IndividualBatchRelease = () => {
  const [load, setLoad] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [rowData, setRowData] = useState([]);
  const [rowLength, setRowLength] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchedValue, setSearchedValue] = useState("");
  const [loadingData, setLoadingData] = useState({
    unBlindedLotData: [],
    depotData: [],
    labelGrpData: [],
    protocolData: [],
  });
  const [unblindedLotSelection, setUnblindedLotSelection] = useState({});
  const [depotSection, setDepotSection] = useState({});
  const [labelSection, setLabelSection] = useState({});
  const [protocolSection, setProtocolSection] = useState({});
  const [stringData, setStringData] = useState("");
  const [showConfirmData, setShowConfirmData] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);

  const gridWrapperRef = useRef(null);

  const navigate = useNavigate();

  const { depotData, labelGrpData, protocolData, unBlindedLotData } =
    loadingData;

  const access_token = localStorage?.getItem("access_token");
  const roleId = localStorage.getItem("roleId");

  useEffect(() => {
    if (page >= 0 && pageSize && access_token) {
      fetchAllIndividualBatchData(
        page,
        pageSize,
        access_token,
        debouncedSearch
      );
    }
  }, [pageSize, page, debouncedSearch]);

  useEffect(() => {
    if (access_token && rowData && rowData?.length > 0) {
      fetchLoadingData(access_token);
    }
  }, [access_token, rowData]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchedValue);
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchedValue]);

  useEffect(() => {
    if (rowData && rowData?.length > 0) {
      const initialSelection = {};
      const initialDepoSection = {};
      const initialLabelSection = {};

      rowData?.forEach((row) => {
        if (row.newLotId) {
          initialSelection[row.id] = row.newLotId;
        }
      });

      rowData?.forEach((row) => {
        if (row.depotId) {
          initialDepoSection[row.id] = row.depotId;
        }
      });

      rowData?.forEach((row) => {
        if (row.labelGroupId) {
          initialLabelSection[row.id] = row.labelGroupId;
        }
      });

      setUnblindedLotSelection(initialSelection);
      setDepotSection(initialDepoSection);
      setLabelSection(initialLabelSection);
    }
  }, [rowData]);

  useEffect(() => {
    const mapping = {
      unblindedLotSelection: "NewLotId",
      depotSection: "NewDepotId",
      labelSection: "NewLabelGroupId",
      protocolSection: "NewProtocolId",
    };

    const allSections = {
      unblindedLotSelection,
      depotSection,
      labelSection,
      protocolSection,
    };

    const finalOutput = transformSections(allSections, mapping);
    setStringData(finalOutput);
  }, [unblindedLotSelection, depotSection, labelSection, protocolSection]);

  const columns = [
    {
      field: "drugTypeName",
      headerName: "Drug Type",
      width: 200,
      renderHeader: () => <div className="grid-heading">{"Drug Type"}</div>,
      renderCell: (params) => {
        const name = params.row.drugTypeName;

        return <span>{name}</span>;
      },
    },
    {
      field: "kitNumber",
      headerName: "Drug Kit Number",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Drug Kit Number"}</div>
      ),
      renderCell: (params) => {
        const kitNumber = params.row.kitNumber;

        return <span>{kitNumber}</span>;
      },
    },
    {
      field: "sequenceNumber",
      headerName: "Sequence Number",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Sequence Number"}</div>
      ),
      renderCell: (params) => {
        const sequenceNumber = params.row.sequenceNumber;

        return <span>{sequenceNumber}</span>;
      },
    },
    {
      field: "unblindedLotNumber",
      headerName: "Current Unblinded Lot Number",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Current Unblinded Lot Number"}</div>
      ),
      renderCell: (params) => {
        const unblindedLotNumber = params.row.unblindedLotNumber;

        return <span>{unblindedLotNumber || "N/A"}</span>;
      },
    },
    {
      field: "newUnblindedLotNumber",
      headerName: "New Unblinded Lot Number",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"New Unblinded Lot Number"}</div>
      ),
      renderCell: (params) => {
        const rowId = params.id;
        const newUnblindedLotNumber = params.row.newLotId;

        const value =
          rowId in unblindedLotSelection
            ? unblindedLotSelection[rowId]
            : newUnblindedLotNumber || "";

        return (
          <UncontrolledSearchSelect
            scrollContainerRef={gridWrapperRef}
            data={unBlindedLotData}
            value={value}
            onChange={(selectedValue) => {
              setUnblindedLotSelection((prev) => ({
                ...prev,
                [rowId]: selectedValue,
              }));
            }}
          />
        );
      },
    },
    {
      field: "depotName",
      headerName: "Current Depot Name",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Current Depot Name"}</div>
      ),
      renderCell: (params) => {
        const depotName = params.row.depotName;

        return <span>{depotName}</span>;
      },
    },
    {
      field: "newDepotName",
      headerName: "New Depot Name",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"New Depot Name"}</div>
      ),
      renderCell: (params) => {
        const rowId = params.id;
        const depotId = params.row.depotId;

        const value =
          rowId in depotSection ? depotSection[rowId] : depotId || "";

        return (
          <UncontrolledSearchSelect
            scrollContainerRef={gridWrapperRef}
            data={depotData}
            value={value}
            onChange={(selectedValue) => {
              setDepotSection((prev) => ({
                ...prev,
                [rowId]: selectedValue,
              }));
            }}
          />
        );
      },
    },
    {
      field: "labelGroupName",
      headerName: "Current Label Group",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Current Label Group"}</div>
      ),
      renderCell: (params) => {
        const labelGroupName = params.row.labelGroupName;

        return <span>{labelGroupName}</span>;
      },
    },
    {
      field: "newLabelGroup",
      headerName: "New Label Group",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"New Label Group"}</div>
      ),
      renderCell: (params) => {
        const rowId = params.id;
        const labelGroupId = params.row.labelGroupId;

        const value =
          rowId in labelSection ? labelSection[rowId] : labelGroupId || "";

        return (
          <UncontrolledSearchSelect
            scrollContainerRef={gridWrapperRef}
            data={labelGrpData}
            value={value}
            onChange={(selectedValue) => {
              setLabelSection((prev) => ({
                ...prev,
                [rowId]: selectedValue,
              }));
            }}
          />
        );
      },
    },
    {
      field: "assignableProtocolNames",
      headerName: "Current Assigned Protocols",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Current Assigned Protocols"}</div>
      ),
      renderCell: (params) => {
        const assignableProtocolNames = params.row.assignableProtocolNames;

        return <span>{assignableProtocolNames || "N/A"}</span>;
      },
    },
    {
      field: "newAssignedProtocol",
      headerName: "New Assigned Protocols",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"New Assigned Protocols"}</div>
      ),
      renderCell: (params) => {
        const rowId = params.id;
        const protocolId = ""; // Can be replaced with params.row.assignableProtocolIds if needed

        const value =
          rowId in protocolSection ? protocolSection[rowId] : protocolId || "";

        return (
          <UncontrolledSearchSelect
            scrollContainerRef={gridWrapperRef}
            data={protocolData}
            value={value}
            onChange={(selectedValue) => {
              setProtocolSection((prev) => ({
                ...prev,
                [rowId]: selectedValue,
              }));
            }}
          />
        );
      },
    },
    {
      field: "quantity",
      headerName: "Current Quantity",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Current Quantity"}</div>
      ),
      renderCell: (params) => {
        const quantity = params.row.quantity;

        return <span>{quantity}</span>;
      },
    },
    {
      field: "quantityToAssign",
      headerName: "Quantity to Assign",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Quantity to Assign"}</div>
      ),
      renderCell: (params) => {
        const quantityToAssign = params.row.quantityToAssign;

        return <span>{quantityToAssign}</span>;
      },
    },
  ];

  const fetchLoadingData = async (access_token) => {
    try {
      setLoad(true);
      const res = await fetchOnLoadDataService(access_token);
      if (res?.status == 200) {
        setLoad(false);
        const data = res?.data?.result;
        setLoadingData({
          depotData: data?.depotsWithUnassign,
          labelGrpData: data?.labelGroupsWithUnassign,
          unBlindedLotData: data?.lotsWithUnassign,
          protocolData: data?.protocols?.filter((item) => item?.value !== ""),
        });
      }
    } catch (err) {
      setLoad(false);
      console.error("Error fetching loading data:", err);
      if (err?.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  const fetchAllIndividualBatchData = async (
    page,
    pageSize,
    access_Token,
    debouncedSearch = ""
  ) => {
    setLoad(true);
    try {
      const res = await fetchAllIndividualDrugService(
        page + 1,
        pageSize,
        access_Token,
        debouncedSearch
      );
      if (res?.status === 200) {
        setRowData(res?.data?.result);
        setRowLength(res?.data?.totalCount);
        setLoad(false);
      } else if (res?.status == 401) {
        navigate("/unauthorize");
      }
    } catch (err) {
      setLoad(false);
      if (err?.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  const handleSearch = useCallback((e) => {
    setSearchedValue(e.target.value);
  }, []);

  function transformSections(sectionObjects, mapping) {
    const result = {};

    for (const [sectionName, mappedKey] of Object.entries(mapping)) {
      const sectionData = sectionObjects[sectionName];

      for (const [id, value] of Object.entries(sectionData)) {
        if (value === "") continue; // Skip empty strings

        if (!result[id]) {
          result[id] = {};
        }

        result[id][mappedKey] = value;
      }
    }

    return result;
  }

  const saveIndividualData = async () => {
    try {
      const newData = {
        drugUpdateJson: JSON.stringify(stringData),
        userId: roleId,
      };
      setLoad(true);
      const res = await saveIndividualDrugDataService(
        access_token,
        newData,
        roleId
      );
      if (res?.status == 200) {
        setLoad(false);
        notifySuccess("Individual Batch Kit Is Saved Successfully!");
        fetchAllIndividualBatchData(
          page,
          pageSize,
          access_token,
          debouncedSearch
        );
        setConfirmationData(res?.data?.result);
        setShowConfirmData(true);
      } else {
        notifyError(res?.status?.message);
        setShowConfirmData(false);
      }
    } catch (err) {
      console.log(err?.message);
      setLoad(false);
      setShowConfirmData(false);
      notifyError(err?.message);
      if (err?.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  return (
    <>
      {showConfirmData ? (
        <IndividualConfirmPage
          setShowConfirm={setShowConfirmData}
          data={confirmationData}
        />
      ) : (
        <>
          <div className="content-body">
            <p
              className="bread-crumbs-link-container"
              style={{ fontWeight: "600" }}
            >
              <Link className="bread-crumbs-link" to="/">
                Home
              </Link>{" "}
              |{" "}
              <Link
                className="bread-crumbs-link"
                to="/drug-assignment-individual-release"
              >
                Drug Assignment - Individual
              </Link>{" "}
              -{" "}
              <b style={{ color: "gray" }}>
                {localStorage?.getItem("studyName")}
              </b>
            </p>
            <Grid2
              container
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%", margin: "0 auto" }}
            >
              <Grid2 item md={6} sx={{ width: "100%" }}>
                <p className="page-heading">Drug Assignment - Batch Release</p>
                <Divider />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "20px",
                    alignItems: "center",
                  }}
                >
                  <p className="page-sub-header">
                    <InfoOutlined className="info-icon" />
                    if you want to make an update to a large number of
                    kits/units, please use the
                    <button
                      className="bread-crumbs-btn"
                      onClick={(e) =>
                        navigate("/drug-assignment-batch-release")
                      }
                      style={{ fontSize: "14px" }}
                    >
                      Bulk update function
                    </button>
                  </p>
                </div>
              </Grid2>
            </Grid2>

            <div
              className="search-bar-container"
              style={{
                display: "flex",
                justifyContent: "end",
                margin: "-1% 1% 1% 1%",
              }}
            >
              <SearchedComponent
                handleSearch={handleSearch}
                placeholder="Batches"
                searchedValue={searchedValue}
              />
            </div>

            {load ? (
              <Loader />
            ) : (
              <GridWithRef
                gridWrapperRef={gridWrapperRef}
                rows={rowData}
                columns={columns}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                length={rowLength}
              ></GridWithRef>
            )}

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/");
                  }}
                >
                  Back
                </button>
              </Grid2>
              <Grid2 item md={6}>
                <button
                  onClick={() => saveIndividualData()}
                  className={
                    isEmptyObject(unblindedLotSelection) &&
                    isEmptyObject(depotSection) &&
                    isEmptyObject(labelSection)
                      ? // &&
                        // isEmptyObject(protocolSection)
                        "disabled-btn"
                      : "create-btn"
                  }
                  disabled={
                    isEmptyObject(unblindedLotSelection) &&
                    isEmptyObject(depotSection) &&
                    isEmptyObject(labelSection)
                    // &&
                    // isEmptyObject(protocolSection)
                  }
                >
                  Save
                </button>
              </Grid2>
            </Grid2>
          </div>
        </>
      )}
    </>
  );
};

export default IndividualBatchRelease;
